import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";

import Header from "./components/Header";
import Footer from "./components/Footer";
import LandingPage from "./pages/LandingPage";
import CaseStudyPage from "./pages/CaseStudyPage";
import PackagePage from "./pages/PackagePage";
import InsightsPage from "./pages/InsightsPage";
import InsightPage from "./pages/InsightPage";
import BuiltByAgentsPage from "./pages/BuiltByAgentsPage";
import FaqPage from "./pages/FaqPage";
import { BookingProvider } from "./context/BookingContext";
import { AssistantProvider } from "./context/AssistantContext";
import BookingModal from "./components/BookingModal";
import AssistantDock from "./components/AssistantDock";
import { useLocaleSync } from "./hooks/useLocaleSync";
import { isSupportedLocale, DEFAULT_LOCALE } from "./i18n/locales";

import "./App.scss";

const ROUTES = (
  <>
    <Route index element={<LandingPage />} />
    <Route path="work/:slug" element={<CaseStudyPage />} />
    <Route path="packages/:slug" element={<PackagePage />} />
    <Route path="insights" element={<InsightsPage />} />
    <Route path="insights/:slug" element={<InsightPage />} />
  </>
);

const LocaleRoot: React.FC = () => {
  const { locale } = useParams<{ locale: string }>();
  if (!locale || !isSupportedLocale(locale)) {
    // Unknown prefix (e.g. /de/foo) — redirect to root rather than looping on the unchanged path
    return <Navigate to="/" replace />;
  }
  if (locale === DEFAULT_LOCALE) {
    // /en/... — strip the /en prefix and redirect to the canonical path; stripLocalePrefix
    // only handles PREFIXED_LOCALES so we strip the default prefix explicitly here
    const rest = window.location.pathname.slice(1 + DEFAULT_LOCALE.length) || '/';
    const target = rest.startsWith('/') ? rest : '/' + rest;
    return <Navigate to={target} replace />;
  }
  return <Routes>{ROUTES}</Routes>;
};

const AppRoutes: React.FC = () => {
  useLocaleSync();
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/work/:slug" element={<CaseStudyPage />} />
      <Route path="/packages/:slug" element={<PackagePage />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/insights/:slug" element={<InsightPage />} />
      <Route path="/built-by-agents" element={<BuiltByAgentsPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/:locale/*" element={<LocaleRoot />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => (
  <BookingProvider>
    <AssistantProvider>
      <div className="app">
        <Header />
        <AppRoutes />
        <Footer />
        <BookingModal />
        <AssistantDock />
      </div>
    </AssistantProvider>
  </BookingProvider>
);

export default App;
